import { Box, Button, Popover } from '@mui/material';
import { useState } from 'react';
import { usePopover } from '../../hooks/usePopover';
import type { CatalogFiltersType } from '../../pages/studio/Catalog/useCatalogTable';
import {
  isFieldActive,
  type FilterFieldConfig,
} from '../CatalogFilterModel/filterModel';
import * as styles from './CatalogFilterBar.styles';
import { ChoiceFilter } from './ChoiceFilter';
import { FilterDropdownButton } from './FilterDropdownButton';
import { RangeFilter } from './RangeFilter';
import { TextFilter } from './TextFilter';
import { ThresholdFilter } from './ThresholdFilter';

interface CatalogFilterBarProps {
  filters: CatalogFiltersType | undefined;
  onFiltersChange: (filters: CatalogFiltersType | undefined) => void;
  /** Which filter fields to render as dropdowns */
  filterFields: FilterFieldConfig[];
}

/** Drop the key when cleared; otherwise return the next filters object. */
const writeFilters = (
  current: Record<string, unknown>,
  patch: Record<string, unknown>,
): CatalogFiltersType | undefined => {
  const next = { ...current };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined || v === null || v === '') delete next[k];
    else next[k] = v;
  }
  return (Object.keys(next).length > 0 ? next : undefined) as
    | CatalogFiltersType
    | undefined;
};

export const CatalogFilterBar = ({
  filters,
  onFiltersChange,
  filterFields,
}: CatalogFilterBarProps) => {
  const popover = usePopover();
  const [openKey, setOpenKey] = useState<string | null>(null);

  const f = (filters ?? {}) as Record<string, unknown>;

  const openField =
    (key: string) => (event: React.MouseEvent<HTMLElement>) => {
      popover.open(event);
      setOpenKey(key);
    };

  const close = () => {
    popover.close();
    setOpenKey(null);
  };

  const setKey = (key: string, value: unknown) => {
    onFiltersChange(writeFilters(f, { [key]: value }));
  };

  const applyPatch = (patch: Record<string, unknown>) => {
    onFiltersChange(writeFilters(f, patch));
  };

  const hasActive = filterFields.some((field) => isFieldActive(filters, field));
  const openFieldConfig =
    filterFields.find((field) => field.key === openKey) ?? null;

  const renderControl = (field: FilterFieldConfig) => {
    if (field.type === 'range') {
      return (
        <RangeFilter field={field} filters={filters} onCommit={applyPatch} />
      );
    }
    if (field.type === 'select') {
      return (
        <ChoiceFilter
          value={(f[field.key] as string | undefined) ?? undefined}
          options={field.options ?? []}
          onChange={(value) => setKey(field.key, value)}
        />
      );
    }
    if (field.type === 'threshold') {
      return (
        <ThresholdFilter
          label={field.label}
          min={field.min ?? 0}
          max={field.max ?? 100}
          step={field.step}
          value={(f[field.key] as number | undefined) ?? undefined}
          onChange={(value) => setKey(field.key, value)}
        />
      );
    }
    return (
      <TextFilter
        label={field.label}
        value={(f[field.key] as string | undefined) ?? ''}
        onChange={(value) => setKey(field.key, value)}
      />
    );
  };

  return (
    <Box data-qa-id="catalog-filter-bar" sx={styles.bar}>
      {filterFields.map((field) => (
        <FilterDropdownButton
          key={field.key}
          label={field.label}
          isActive={isFieldActive(filters, field)}
          onClick={openField(field.key)}
        />
      ))}
      <Button
        variant="text"
        size="small"
        disabled={!hasActive}
        onClick={() => onFiltersChange(undefined)}
        sx={styles.clearAllButton}
      >
        Clear all
      </Button>
      <Popover
        open={popover.isOpen && openFieldConfig != null}
        anchorEl={popover.anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: styles.popoverPaper } }}
      >
        {openFieldConfig && renderControl(openFieldConfig)}
      </Popover>
    </Box>
  );
};
